import { useState } from "react";
import { ChevronDown, Download, RefreshCw } from "lucide-react";
import { TransactionProvider } from "@/components/transactions/TransactionProvider";
import { useTransactionContext } from "@/components/transactions/TransactionContext";
import { TransactionFilters } from "@/components/transactions/TransactionFilters";
import { TransactionTable } from "@/components/transactions/TransactionTable";
import { ClassifyProgress } from "@/components/transactions/ClassifyProgress";
import { useRecategorizeTransactions } from "@/hooks/useRecategorizeTransactions";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { TransactionDateRangePicker } from "@/components/transactions/TransactionDateRangePicker";
import { coerceSupportedCurrency } from "@/lib/currency";
import {
  downloadTransactionsCsv,
  fetchAllTransactionsForExport,
} from "@/utils/transactionsCsvExport";
function TransactionsAllCard() {
  const {
    startRecategorize,
    status: recategorizeStatus,
    progress: recategorizeProgress,
    error: recategorizeError,
    info: recategorizeInfo,
    isBusy: recategorizeBusy,
  } = useRecategorizeTransactions();
  const [showAutoCategorizeConfirm, setShowAutoCategorizeConfirm] =
    useState(false);

  async function handleConfirmAutoCategorize() {
    await startRecategorize();
    setShowAutoCategorizeConfirm(false);
  }

  return (
    <div className="w-full rounded-xl border border-[#d5dde7] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5ebf2] px-5 py-3">
        <h2 className="text-base font-semibold text-[#2d3f57]">
          All Transactions
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAutoCategorizeConfirm(true)}
            disabled={recategorizeBusy}
            className="inline-flex h-8 items-center gap-2 rounded-md border border-[#d5dde7] bg-white px-2.5 text-sm font-medium text-[#2d3f57] hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${recategorizeBusy ? "animate-spin" : ""}`}
              aria-hidden
            />
            {recategorizeBusy ? "Refreshing…" : "Refresh Keywords"}
          </button>
        </div>
      </div>
      {recategorizeStatus && (
        <div className="border-b border-slate-100 px-6 py-3">
          <ClassifyProgress
            status={recategorizeStatus}
            progress={recategorizeProgress}
          />
        </div>
      )}
      {recategorizeError && (
        <div className="border-b border-red-100 bg-red-50 px-6 py-2 text-sm text-red-700">
          {recategorizeError}
        </div>
      )}
      {recategorizeInfo && (
        <div className="border-b border-slate-100 bg-slate-50 px-6 py-2 text-sm text-slate-600">
          {recategorizeInfo}
        </div>
      )}
      <TransactionFilters />
      <TransactionTable />
      <ConfirmationModal
        open={showAutoCategorizeConfirm}
        title="Run auto-categorization?"
        description="This only affects transactions that are currently uncategorized. Already categorized or manually edited transactions will not change."
        confirmLabel="Auto Categorize"
        confirmTone="primary"
        isLoading={recategorizeBusy}
        onCancel={() => setShowAutoCategorizeConfirm(false)}
        onConfirm={handleConfirmAutoCategorize}
      />
    </div>
  );
}

function TransactionsPageInner() {
  const { filters, updateFilter, commitDateRange } = useTransactionContext();
  const [exporting, setExporting] = useState(false);

  async function handleExport() {
    setExporting(true);
    try {
      const rows = await fetchAllTransactionsForExport(filters);
      downloadTransactionsCsv(rows);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex w-full flex-col items-center gap-5 pb-10 pt-2">
      <header className="w-full max-w-7xl rounded-xl border border-[#d5dde7] bg-white px-3 py-3 sm:px-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <h1 className="text-[34px] font-semibold leading-10 text-[#0f172b]">
              Transactions
            </h1>
            <p className="text-base leading-6 text-[#6b7f99]">
              View and categorize all your financial transactions
            </p>
          </div>
          <div className="flex w-full flex-wrap items-center justify-center gap-2 lg:w-auto">
            <label className="text-sm text-[#52667f]">Currency:</label>
            <div className="relative">
              <select
                value={filters.currency ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  updateFilter(
                    "currency",
                    value ? coerceSupportedCurrency(value) : undefined,
                  );
                }}
                className="h-9 appearance-none rounded-md border border-[#d9e1ea] bg-[#f4f7fb] py-2 pl-3 pr-8 text-sm font-medium text-[#2d3f57] outline-none ring-blue-500 focus:ring-2"
              >
                <option value="">All</option>
                <option value="USD">USD</option>
                <option value="ILS">ILS</option>
                <option value="EUR">EUR</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6b7f99]" />
            </div>

            <span className="ml-2 text-sm text-[#52667f]">Dates:</span>
            <TransactionDateRangePicker
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              commitDateRange={commitDateRange}
            />

            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="ml-2 inline-flex h-9 items-center gap-2 rounded-md border border-[#d5dde7] bg-[#f8fafc] px-3 text-sm font-medium text-[#2d3f57] hover:bg-slate-100 disabled:opacity-60"
            >
              <Download className="h-4 w-4" aria-hidden />
              {exporting ? "Exporting…" : "Export"}
            </button>
          </div>
        </div>
      </header>

      <div className="w-full max-w-7xl">
        <TransactionsAllCard />
      </div>
    </div>
  );
}

export default function TransactionsPage() {
  return (
    <TransactionProvider>
      <TransactionsPageInner />
    </TransactionProvider>
  );
}
